import {
    EzException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzBoolean,
    EzString
} from '/ezlibrary/helpers/EzHelpers.js';

import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';


/**
 * @class
 * @extends {EzEnumeration2}
 * @description
 * A template for an ezClocker enumeration class.
 * ------------------------------------------------------------------
 * Import into non-enumeration classes with:
 *      import {
 *          // ... other enumeration classes ...
 *          EzClockerAccountType
 *      } from '/ezlibrary/enums/EzEnumerations.js';
 * ------------------------------------------------------------------
 * Import into other enumeration classes or classes that cannot import EzEnumerations:
 *      import { EzClockerAccountType } from '/ezlibrary/EzClockerAccountType.js';
 * ------------------------------------------------------------------
 * Access static reference:
 *      Inside class: EzClockerAccountType.{method or property}
 *      Outside of class: EzClockerAccountType.{method or property}
 * ------------------------------------------------------------------
 */
export class EzClockerAccountType extends EzEnumeration2 {
    /**
     * @static
     * @public @readonly @property
     * Gets an array of ezClocker admin account types
     *
     * @returns {array}
     */
    static get ezAdminAccountTypes() {
        return [
            EzClockerAccountType.ADMIN
        ];
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an array of ezClocker support account types
     *
     * @returns {array}
     */
    static get ezSupportAccountTypes() {
        return [
            EzClockerAccountType.SUPPORT
        ];
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an array of developer account types
     *
     * @returns {array}
     */
    static get ezDeveloperAccountTypes() {
        return [
            EzClockerAccountType.DEVELOPER
        ];
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an array of employer account types
     *
     * @returns {array}
     */
    static get ezEmployerAccountTypes() {
        return [
            EzClockerAccountType.EMPLOYER
        ];
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an array of 'acting as employer' account types
     *
     * @returns {array}
     */
    static get ezActingAsEmployerAccountTypes() {
        return [
            EzClockerAccountType.PAYROLL_MANAGER_AS_EMPLOYER,
            EzClockerAccountType.MANAGER_AS_EMPLOYER
        ];
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an array of payroll manager account types
     *
     * @returns {array}
     */
    static get ezPayrollManagerAccountTypes() {
        return [
            EzClockerAccountType.PAYROLL_MANAGER
        ];
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an array of manager account types
     *
     * @returns {array}
     */
    static get ezManagerAccountTypes() {
        return [
            EzClockerAccountType.PAYROLL_MANAGER,
            EzClockerAccountType.MANAGER
        ];
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an array of employee account types
     *
     * @returns {array}
     */
    static get ezEmployeeAccountTypes() {
        return [
            EzClockerAccountType.PAYROLL_MANAGER,
            EzClockerAccountType.MANAGER,
            EzClockerAccountType.EMPLOYEE,
            EzClockerAccountType.PERSONAL
        ];
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an array of personal account types
     *
     * @returns {array}
     */
    static get ezPersonalAccountTypes() {
        return [
            EzClockerAccountType.PERSONAL
        ];
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the EzClockerAccountType for the current user context.
     * Requires a valid globalThis.ezApi.ezclocker?.ezClockerContext?.ezGetUserContext() to call this method.
     * @returns {string}
     * A valid enumeration property value from EzClockerAccountType
     */
    static get ezCurrentUserEzClockerAccountType() {
        if (!EzObject.isValid(globalThis.ezApi.ezclocker?.ezClockerContext?.ezGetUserContext())) {
            throw new EzException(
                EzString.em`
                    A valid user context from globalThis.ezApi.ezclocker?.ezClockerContext?.ezGetUserContext() is required
                    before calling EzClockerAccountType.ezCurrentUserEzClockerAccountType.`);
        }

        const userContext = globalThis.ezApi.ezclocker.ezClockerContext.ezGetUserContext();

        const selectedEmployerIsPersonalEmployer = globalThis.ezApi.ezclocker?.ezClockerContext?.ezPersonalEmployerId ===
            globalThis.ezApi.ezclocker.ezClockerContext?.ezGetSelectedEmployerAccount()?.id;

        if (EzBoolean.isTrue(userContext?.isEmployer)) {
            return EzClockerAccountType.EMPLOYER;
        } else if (EzBoolean.isTrue(userContext?.isPayrollManager)) {
            return EzBoolean.isTrue(globalThis.ezApi.ezclocker.ezClockerContext?.ezGetSelectedEmployerAccount()?.ready)
                ? EzClockerAccountType.PAYROLL_MANAGER_AS_EMPLOYER
                : EzClockerAccountType.PAYROLL_MANAGER;
        } else if (EzBoolean.isTrue(userContext?.isManager)) {
            return EzBoolean.isTrue(globalThis.ezApi.ezclocker.ezClockerContext?.ezGetSelectedEmployerAccount()?.ready)
                ? EzClockerAccountType.MANAGER_AS_EMPLOYER
                : EzClockerAccountType.MANAGER;
        } else if (EzBoolean.isTrue(userContext.isEmployee) && EzBoolean.isFalse(selectedEmployerIsPersonalEmployer)) {
            return EzClockerAccountType.EMPLOYEE;
        } else if (EzBoolean.isTrue(userContext.isPersonal) ||
            (EzBoolean.isTrue(userContext.isEmployee) && EzBoolean.isTrue(selectedEmployerIsPersonalEmployer))) {
            return EzClockerAccountType.PERSONAL;
        } else if (EzBoolean.isTrue(userContext.isSupport)) {
            return EzClockerAccountType.SUPPORT;
        } else if (EzBoolean.isTrue(userContext.isDeveloper)) {
            return EzClockerAccountType.DEVELOPER;
        } else if (EzBoolean.isTrue(userContext.isAdmin)) {
            return EzClockerAccountType.ADMIN;
        }

        return EzClockerAccountType.UNKNOWN;
    }

    /**
     * @static
     * @public @method
     *
     * @param {string} ezClockerAccountType
     * Default: EzClockerAccountType.UNKNOWN
     * A valid enumeration property value from EzClockerAccountType
     *
     * @returns {string}
     */
    static ezToDisplayName(ezClockerAccountType = EzClockerAccountType.UNKNOWN) {
        return EzString.stringOrDefault(
            EzClockerAccountType.ezEnumData(ezClockerAccountType.toUpperCase())?.display,
            'UNKNOWN');
    }

    /**
     * @static
     * @public @method
     * Gets the account type name for the enumeration property value
     *
     * @param {string} ezClockerAccountType
     * Default: EzClockerAccountType.UNKNOWN
     * A valid enumeration property value from EzClockerAccountType
     *
     * @returns {string}
     */
    static ezToAccountTypeName(ezClockerAccountType = EzClockerAccountType.UNKNOWN) {
        return EzString.stringOrDefault(
            EzClockerAccountType.ezEnumData(ezClockerAccountType.toUpperCase())?.accountTypeName,
            'UNKNOWN');
    }

    /**
     * @static
     * @public @method
     *
     * @param {string} ezClockerAccountType
     * Default: EzClockerAccountType.UNKNOWN
     * A valid enumeration property value from EzClockerAccountType
     *
     * @returns {number}
     */
    static ezToAccountLevel(ezClockerAccountType = EzClockerAccountType.UNKNOWN) {
        return EzString.stringOrDefault(
            EzClockerAccountType.ezEnumData(ezClockerAccountType.toUpperCase())?.accountLevel,
            'UNKNOWN');
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @private @field
     * Stores the singleton instance of this enumeration class.
     *
     * @type {EzClockerAccountType}
     */
    static #ezClockerAccountTypeSingleton = null;

    /**
     * @static
     * @private @method
     * Initializes the static properties, fields, and methods for the enumeration class EzClockerAccountType
     */
    static #ezInit() {
        this.#ezClockerAccountTypeSingleton = new EzClockerAccountType();

        this.#ezClockerAccountTypeSingleton.ezBuildEnumProp(
            'UNKNOWN',
            'UNKNOWN',
            {
                value: 'UNKNOWN',
                display: 'UNKNOWN',
                accountTypeName: 'UNKNOWN',
                accountLevel: 0
            });

        this.#ezClockerAccountTypeSingleton.ezBuildEnumProp(
            'ADMIN',
            'ADMIN',
            {
                value: 'ADMIN',
                display: 'Admin',
                accountTypeName: 'admin',
                accountLevel: 1
            });

        this.#ezClockerAccountTypeSingleton.ezBuildEnumProp(
            'SUPPORT',
            'SUPPORT',
            {
                value: 'SUPPORT',
                display: 'Support',
                accountTypeName: 'support',
                accountLevel: 2
            });

        this.#ezClockerAccountTypeSingleton.ezBuildEnumProp(
            'DEVELOPER',
            'DEVELOPER',
            {
                value: 'DEVELOPER',
                display: 'Developer',
                accountTypeName: 'developer',
                accountLevel: 3
            });

        this.#ezClockerAccountTypeSingleton.ezBuildEnumProp(
            'EMPLOYER',
            'EMPLOYER',
            {
                value: 'EMPLOYER',
                display: 'Employer',
                accountTypeName: 'employer',
                accountLevel: 4
            });

        this.#ezClockerAccountTypeSingleton.ezBuildEnumProp(
            'PAYROLL_MANAGER',
            'PAYROLL_MANAGER',
            {
                value: 'PAYROLL_MANAGER',
                display: 'Payroll Manager',
                accountTypeName: 'payroll_manager',
                accountLevel: 5
            });

        this.#ezClockerAccountTypeSingleton.ezBuildEnumProp(
            'PAYROLL_MANAGER_AS_EMPLOYER',
            'PAYROLL_MANAGER_AS_EMPLOYER',
            {
                value: 'PAYROLL_MANAGER_AS_EMPLOYER',
                display: 'Payroll Manager',
                accountTypeName: 'payroll_manager',
                accountLevel: 5
            });

        this.#ezClockerAccountTypeSingleton.ezBuildEnumProp(
            'MANAGER',
            'MANAGER',
            {
                value: 'MANAGER',
                display: 'Manager',
                accountTypeName: 'manager',
                accountLevel: 6
            });

        this.#ezClockerAccountTypeSingleton.ezBuildEnumProp(
            'MANAGER_AS_EMPLOYER',
            'MANAGER_AS_EMPLOYER',
            {
                value: 'MANAGER_AS_EMPLOYER',
                display: 'Manager',
                accountTypeName: 'manager',
                accountLevel: 6
            });


        this.#ezClockerAccountTypeSingleton.ezBuildEnumProp(
            'EMPLOYEE',
            'EMPLOYEE',
            {
                value: 'EMPLOYEE',
                display: 'Employee',
                accountTypeName: 'employee',
                accountLevel: 7
            });

        this.#ezClockerAccountTypeSingleton.ezBuildEnumProp(
            'PERSONAL',
            'PERSONAL',
            {
                value: 'PERSONAL',
                display: 'Personal',
                accountTypeName: 'personal',
                accountLevel: 8
            });
    }

    /**
     * @static
     * Static Initialization of Enumeration
     */
    static {
        if (null == EzClockerAccountType.#ezClockerAccountTypeSingleton) {
            EzClockerAccountType.#ezInit();
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}